// v3.15-925: Upcoming Appointments dashboard widget.
// v3.15-1130: now reads BOTH /api/appointments/upcoming AND the new
// /api/appointments/operations-summary so the widget surfaces:
//   • Starting Soon (<= 20 min)  — red urgency tier
//   • Overdue (no outcome logged) — amber urgency tier
//   • Today / Tomorrow / Future   — normal list
// Click-through navigates to the client detail page. Internal appointments
// system — no Google Calendar dependency.
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, ChevronRight, Loader2, AlertTriangle, Zap } from 'lucide-react';
import api from '../lib/api';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return iso; }
};
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const Row = ({ item, showDate, onClick, tone = 'default' }) => {
  const toneStyles = {
    default: 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300',
    soon:    'border-red-300 bg-red-50 hover:bg-red-100 hover:border-red-400',
    overdue: 'border-amber-300 bg-amber-50 hover:bg-amber-100 hover:border-amber-400',
  };
  const iconStyles = {
    default: 'bg-blue-50 text-blue-600',
    soon:    'bg-red-100 text-red-700',
    overdue: 'bg-amber-100 text-amber-700',
  };
  const Icon = tone === 'overdue' ? AlertTriangle : (tone === 'soon' ? Zap : Clock);
  return (
    <button
      type="button"
      onClick={() => onClick?.(item)}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors text-left ${toneStyles[tone]}`}
      data-testid={`upcoming-appt-${item.id}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconStyles[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{item.client_name || 'Unknown client'}</div>
        <div className="text-[11px] text-slate-600 truncate">
          {showDate ? `${formatDate(item.date_time_start)} • ` : ''}
          {formatTime(item.date_time_start)}
          {item.assigned_to_name ? ` • ${item.assigned_to_name}` : ''}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
    </button>
  );
};

const Section = ({ title, count, items, showDate, onClick, emptyLabel, tone = 'default', testIdSlug }) => (
  <div data-testid={`upcoming-section-${(testIdSlug || title.toLowerCase()).replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-1.5">
      <div className={`text-xs font-bold uppercase tracking-wide ${
        tone === 'soon' ? 'text-red-700' :
        tone === 'overdue' ? 'text-amber-700' :
        'text-slate-600'
      }`}>
        {title}
      </div>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
        tone === 'soon' ? 'text-red-700 bg-red-100' :
        tone === 'overdue' ? 'text-amber-700 bg-amber-100' :
        'text-slate-500 bg-slate-100'
      }`}>{count}</span>
    </div>
    {items.length === 0 ? (
      <div className="text-[12px] text-slate-400 italic px-1 py-1">{emptyLabel}</div>
    ) : (
      <div className="space-y-1.5">
        {items.slice(0, 4).map((i) => (
          <Row key={i.id} item={i} showDate={showDate} onClick={onClick} tone={tone} />
        ))}
        {items.length > 4 && (
          <div className="text-[11px] text-slate-500 px-2 pt-0.5">
            +{items.length - 4} more
          </div>
        )}
      </div>
    )}
  </div>
);

export default function UpcomingAppointmentsWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ today: [], tomorrow: [], future: [], counts: { today: 0, tomorrow: 0, future: 0 } });
  // v3.15-1130: ops = starting_soon / overdue / missed buckets
  const [ops, setOps] = useState({ starting_soon: [], overdue: [], missed: [], counts: { starting_soon: 0, overdue: 0, missed_7d: 0 } });

  const fetchData = useCallback(async () => {
    try {
      const [resUp, resOps] = await Promise.all([
        api.get('/appointments/upcoming'),
        api.get('/appointments/operations-summary'),
      ]);
      setData(resUp.data || { today: [], tomorrow: [], future: [], counts: {} });
      setOps(resOps.data || { starting_soon: [], overdue: [], missed: [], counts: {} });
    } catch {
      // soft-fail: keep empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60_000);  // v3.15-1130: tighter refresh (60s)
    return () => clearInterval(id);
  }, [fetchData]);

  const total =
    (data.counts?.today || 0) +
    (data.counts?.tomorrow || 0) +
    (data.counts?.future || 0) +
    (ops.counts?.starting_soon || 0) +
    (ops.counts?.overdue || 0);

  const handleClick = (item) => {
    if (item.client_id) navigate(`/clients/${item.client_id}`);
  };

  return (
    <div
      className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100"
      data-testid="upcoming-appointments-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Calendar className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Appointments</h3>
            <p className="text-[11px] text-slate-500">Starting Soon · Overdue · Today · Tomorrow · Future</p>
          </div>
        </div>
        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-full" data-testid="upcoming-total-count">
          {total} active
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-slate-400 text-xs">
          <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Loading…
        </div>
      ) : total === 0 ? (
        <div
          className="text-center py-8 text-slate-400 text-sm"
          data-testid="upcoming-appointments-empty"
        >
          No upcoming appointments.
        </div>
      ) : (
        <div className="space-y-4">
          {(ops.counts?.starting_soon || 0) > 0 && (
            <Section
              title="Starting Soon"
              testIdSlug="starting-soon"
              count={ops.counts?.starting_soon || 0}
              items={ops.starting_soon || []}
              emptyLabel="—"
              tone="soon"
              onClick={handleClick}
            />
          )}
          {(ops.counts?.overdue || 0) > 0 && (
            <Section
              title="Overdue (No Outcome)"
              testIdSlug="overdue"
              count={ops.counts?.overdue || 0}
              items={ops.overdue || []}
              emptyLabel="—"
              tone="overdue"
              onClick={handleClick}
            />
          )}
          <Section
            title="Today"
            count={data.counts?.today || 0}
            items={data.today || []}
            emptyLabel="Nothing scheduled today."
            onClick={handleClick}
          />
          <Section
            title="Tomorrow"
            count={data.counts?.tomorrow || 0}
            items={data.tomorrow || []}
            emptyLabel="Nothing scheduled tomorrow."
            onClick={handleClick}
          />
          <Section
            title="Future"
            count={data.counts?.future || 0}
            items={data.future || []}
            showDate
            emptyLabel="No future appointments."
            onClick={handleClick}
          />
        </div>
      )}

      {/* v3.15-1130: subtle "missed in last 7 days" tally so reps see the pattern */}
      {(ops.counts?.missed_7d || 0) > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]" data-testid="missed-7d-tally">
          <span className="text-slate-500">Missed (last 7 days)</span>
          <span className="font-semibold text-slate-700">{ops.counts.missed_7d}</span>
        </div>
      )}
    </div>
  );
}

